// Inline numbered stepper for the tablet/desktop crumb bar (KAN-763). Same
// three states and tokens as ManualInvoiceStepIndicator's pills, laid out
// horizontally with dash connectors: done = filled brand circle + check,
// current = brand outline + dark label, upcoming = grey.
import { Check } from 'lucide-react';
import { foundation } from '../../../ui';

const fc = foundation.color;
const sp = foundation.spacePx;
const fs = foundation.fontSize;
const fw = foundation.fontWeight;

const S = {
  list: {
    display: 'flex',
    alignItems: 'center',
    gap: sp[2],
    margin: 0,
    padding: 0,
    listStyle: 'none',
    flexShrink: 0,
    fontFamily: foundation.fontFamily.sans,
  },
  item: { display: 'inline-flex', alignItems: 'center', gap: sp[2], whiteSpace: 'nowrap' },
  dot: {
    width: 24,
    height: 24,
    flexShrink: 0,
    borderRadius: foundation.radii.full,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: fs.xs,
    fontWeight: fw.bold,
    lineHeight: 1,
    boxSizing: 'border-box',
    transition: 'background-color 180ms ease, border-color 180ms ease, color 180ms ease',
  },
  dash: { width: 24, height: 2, borderRadius: foundation.radii.full, transition: 'background-color 240ms ease' },
};

function dotStyle(state) {
  if (state === 'done') {
    return { ...S.dot, background: fc.brand.primary, border: `1px solid ${fc.brand.primary}`, color: fc.brand.onPrimary };
  }
  if (state === 'current') {
    return { ...S.dot, background: fc.neutral.surface, border: `2px solid ${fc.brand.primary}`, color: fc.brand.text };
  }
  return { ...S.dot, background: fc.neutral.surface, border: `1px solid ${fc.neutral.border}`, color: fc.neutral.textMuted };
}

function labelStyle(state) {
  return {
    fontSize: fs.sm,
    fontWeight: state === 'current' ? fw.semibold : fw.medium,
    color: state === 'current' ? fc.neutral.text : state === 'done' ? fc.neutral.textSecondary : fc.neutral.textMuted,
    transition: 'color 180ms ease',
  };
}

/**
 * @param {object} props
 * @param {{key: string, label: string}[]} props.steps
 * @param {number} props.currentStep - 1-based.
 * @param {string} [props.ariaLabel]
 */
export default function FullScreenInlineStepper({ steps, currentStep, ariaLabel, testId = 'fullscreen-flow-stepper' }) {
  const list = steps || [];
  return (
    <ol style={S.list} aria-label={ariaLabel} data-testid={testId} data-step={currentStep} data-total={list.length}>
      {list.map((step, i) => {
        const idx = i + 1;
        const state = idx < currentStep ? 'done' : idx === currentStep ? 'current' : 'upcoming';
        return (
          <li key={step.key} style={S.item}>
            <span
              style={S.item}
              aria-current={state === 'current' ? 'step' : undefined}
              data-state={state}
              data-testid={`${testId}-step-${step.key}`}
            >
              <span style={dotStyle(state)} aria-hidden>
                {state === 'done' ? <Check size={14} strokeWidth={2.5} /> : idx}
              </span>
              <span style={labelStyle(state)}>{step.label}</span>
            </span>
            {i < list.length - 1 ? (
              <span
                aria-hidden
                style={{ ...S.dash, background: idx < currentStep ? fc.brand.primary : fc.neutral.border }}
              />
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}
